import React, { useEffect, useState } from "react";
import { AvatarChip } from "./AvatarChip";
import { fetchLeaderboard, fetchLeaderboardGames, fetchLeaderboardArchive } from "../lib/api";
import { VerifiedCheck } from "./VerifiedCheck";

/**
 * LeaderboardsSection — per-game leaderboards with weekly reset.
 *
 * - `WEEK` shows the current Monday-to-Sunday window.
 * - `ALL-TIME` aggregates the entire score_events history.
 * - Game picker is dynamic — only surfaces games that have any events.
 */
export function LeaderboardsSection({ onToast }) {
  const [games, setGames] = useState([]);
  const [game, setGame] = useState("");
  const [period, setPeriod] = useState("week");
  const [data, setData] = useState({ rows: [], week_label: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboardGames().then(setGames).catch(() => setGames([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { period };
    if (game) params.game = game;
    fetchLeaderboard(params)
      .then((d) => setData(d || { rows: [] }))
      .catch((e) => { onToast?.(e?.response?.data?.detail || "Couldn't load leaderboard."); setData({ rows: [] }); })
      .finally(() => setLoading(false));
  }, [game, period, onToast]);

  return (
    <section data-testid="leaderboards-section" className="relative max-w-6xl mx-auto px-6 py-16">
      <div className="text-center mb-10">
        <span className="px-4 py-1.5 text-[10px] font-bold tracking-[3px] bg-[#14b8a6]/15 text-[#14b8a6] rounded-full neon-border-[#14b8a6]">
          POWER RANKINGS
        </span>
        <h2 className="text-5xl md:text-7xl font-display grad-text mt-5">LEADERBOARDS</h2>
        <p className="text-base text-[#a3a3ac] mt-3 max-w-2xl mx-auto">
          Per-game weekly leaderboard — resets every Monday 00:00 UTC.
          Record match results to climb.
        </p>
        {period === "week" && data.week_label && (
          <p className="text-[10px] tracking-widest text-[#14b8a6] mt-1" data-testid="leaderboard-week-label">
            {data.week_label}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-2 mb-6" data-testid="leaderboard-filters">
        <div className="flex rounded-2xl border border-[#14b8a6]/30 overflow-hidden">
          <button
            type="button"
            onClick={() => setPeriod("week")}
            data-testid="leaderboard-period-week"
            className={`px-4 py-2 text-[11px] tracking-widest font-bold ${
              period === "week" ? "bg-[#14b8a6] text-black" : "text-[#14b8a6] hover:bg-[#14b8a6]/10"
            }`}
          >
            WEEK
          </button>
          <button
            type="button"
            onClick={() => setPeriod("all")}
            data-testid="leaderboard-period-all"
            className={`px-4 py-2 text-[11px] tracking-widest font-bold ${
              period === "all" ? "bg-[#14b8a6] text-black" : "text-[#14b8a6] hover:bg-[#14b8a6]/10"
            }`}
          >
            ALL-TIME
          </button>
        </div>
        <select
          value={game}
          onChange={(e) => setGame(e.target.value)}
          data-testid="leaderboard-game-picker"
          className="bg-black/50 border border-[#14b8a6]/30 rounded-2xl px-3 py-2 text-white text-sm"
        >
          <option value="">ALL GAMES</option>
          {games.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>

      <div className="glass-strong neon-border-[#14b8a6] rounded-3xl overflow-hidden" data-testid="leaderboard-table">
        {loading ? (
          <p className="text-center py-10 text-white/55 text-sm">Loading rankings…</p>
        ) : data.rows.length === 0 ? (
          <p className="text-center py-10 text-white/55 text-sm" data-testid="leaderboard-empty">
            No match results recorded yet. Be first — enter a result from an upcoming game card.
          </p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-[9px] tracking-widest text-[#14b8a6] border-b border-[#14b8a6]/20">
                <th className="text-left px-4 py-3 w-12">#</th>
                <th className="text-left px-2 py-3">OPERATOR</th>
                <th className="text-right px-3 py-3">W</th>
                <th className="text-right px-3 py-3">L</th>
                <th className="text-right px-3 py-3">MATCHES</th>
                <th className="text-right px-4 py-3 text-[#14b8a6]">POINTS</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr
                  key={r.user_id}
                  data-testid={`leaderboard-row-${r.user_id}`}
                  className={`border-b border-white/5 hover:bg-[#14b8a6]/5 ${
                    r.rank <= 3 ? "bg-gradient-to-r from-[#14b8a6]/10 to-transparent" : ""
                  }`}
                >
                  <td className={`px-4 py-3 font-display text-lg ${
                    r.rank === 1 ? "text-[#14b8a6]"
                    : r.rank === 2 ? "text-white/80"
                    : r.rank === 3 ? "text-[#14b8a6]"
                    : "text-white/40"
                  }`}>
                    {r.rank === 1 ? "🥇" : r.rank === 2 ? "🥈" : r.rank === 3 ? "🥉" : `#${r.rank}`}
                  </td>
                  <td className="px-2 py-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <AvatarChip avatarId={r.avatar_id} pfpUrl={r.pfp_url} size={32} />
                      <div className="min-w-0">
                        <p className="text-sm font-bold truncate flex items-center gap-1.5">
                          <span className="truncate">{r.name || "Operator"}</span>
                          <VerifiedCheck user={r} size={13} />
                          {(r.user_type === "pro" || r.plan === "elite") && (
                            <span className="text-[9px] tracking-widest bg-[#14b8a6]/20 text-[#14b8a6] px-1.5 py-0.5 rounded-full border border-[#14b8a6]/40">
                              PRO
                            </span>
                          )}
                        </p>
                        <p className="text-[10px] text-white/45">MMR {r.elo || "—"}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right text-sm font-bold text-[#14b8a6]">{r.wins}</td>
                  <td className="px-3 py-3 text-right text-sm font-bold text-[#14b8a6]">{r.losses}</td>
                  <td className="px-3 py-3 text-right text-sm text-white/70">{r.matches}</td>
                  <td className="px-4 py-3 text-right font-display text-lg text-[#14b8a6]">{r.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ArchivePanel game={game} onToast={onToast} />
    </section>
  );
}

export default LeaderboardsSection;

// ---------------------------------------------------------------------------
// ArchivePanel — past 12 weeks' champions per game.
// ---------------------------------------------------------------------------
function ArchivePanel({ game, onToast }) {
  const [archive, setArchive] = useState({ weeks: [] });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const params = { weeks: 12 };
    if (game) params.game = game;
    fetchLeaderboardArchive(params)
      .then((d) => setArchive(d || { weeks: [] }))
      .catch((e) => { onToast?.(e?.response?.data?.detail || "Couldn't load archive."); setArchive({ weeks: [] }); })
      .finally(() => setLoading(false));
  }, [game, onToast, open]);

  return (
    <div data-testid="leaderboard-archive" className="mt-8">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="leaderboard-archive-toggle"
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 rounded-2xl border border-[#14b8a6]/25 bg-[#14b8a6]/5 px-4 py-3 text-left hover:bg-[#14b8a6]/10 transition-colors"
      >
        <span>
          <span className="block text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
            <i className="fas fa-clock-rotate-left mr-1" />WEEKLY CHAMPIONS · LAST 12 WEEKS
          </span>
          <span className="block text-[11px] text-white/55 mt-0.5">
            Past weeks' #1 operators {game ? `for ${game}` : "across every game"}.
          </span>
        </span>
        <i className={`fas fa-chevron-${open ? "up" : "down"} text-[#14b8a6]`} />
      </button>

      {open && (
        <div className="mt-3 rounded-2xl border border-[#14b8a6]/15 bg-black/30 overflow-hidden">
          {loading ? (
            <p className="text-center py-6 text-white/55 text-sm">Loading archive…</p>
          ) : archive.weeks.length === 0 ? (
            <p
              className="text-center py-6 text-white/55 text-sm"
              data-testid="leaderboard-archive-empty"
            >
              No completed weeks yet — record matches to populate the history.
            </p>
          ) : (
            <ul className="divide-y divide-white/5">
              {archive.weeks.map((w) => {
                const c = w.champion || {};
                return (
                  <li
                    key={w.starts_at}
                    data-testid={`leaderboard-archive-row-${w.starts_at}`}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-[#14b8a6]/5"
                  >
                    <span className="font-display text-base text-[#14b8a6] min-w-[28px]">🥇</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] tracking-widest text-[#14b8a6]">
                        {w.week_label}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5 min-w-0">
                        <AvatarChip avatarId={c.avatar_id} pfpUrl={c.pfp_url} size={26} />
                        <p className="text-sm font-bold text-white truncate flex items-center gap-1.5">
                          <span className="truncate">{c.name || "Operator"}</span>
                          <VerifiedCheck user={c} size={12} />
                          {(c.user_type === "pro" || c.plan === "elite") && (
                            <span className="text-[9px] tracking-widest bg-[#14b8a6]/20 text-[#14b8a6] px-1.5 py-0.5 rounded-full border border-[#14b8a6]/40">
                              PRO
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="text-right text-[10px] text-white/60">
                      <p className="font-display text-base text-[#14b8a6]">{c.points}</p>
                      <p>{c.wins}W · {c.losses}L</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
